<script setup lang="ts">
  import { computed, ref } from "vue";
  import { useDebounceFn } from "@vueuse/core";

  import { Select, type SelectOption } from "@/components/episto-ui/select";
  import { useAccountsStore } from "@stores/accounts";
  import type { HubspotCompany } from "@/types";

  const company = defineModel<HubspotCompany | null>({ default: null });

  const accountsStore = useAccountsStore();
  const companies = ref<HubspotCompany[]>([]);
  const loading = ref(false);
  let latestQuery = "";

  const options = computed<SelectOption[]>(() => {
    const selected = company.value;
    const list = selected && !companies.value.some(({ id }) => id === selected.id) ? [selected, ...companies.value] : companies.value;
    return list.map(({ id, name }) => ({ value: id, label: name }));
  });

  const search = useDebounceFn(async (query: string) => {
    latestQuery = query;
    if (!query.trim()) {
      companies.value = [];
      loading.value = false;
      return;
    }

    loading.value = true;
    try {
      const results = await accountsStore.searchHubspotCompanies(query.trim());
      if (query === latestQuery) companies.value = results;
    } catch (error) {
      console.error(error);
      if (query === latestQuery) companies.value = [];
    } finally {
      if (query === latestQuery) loading.value = false;
    }
  }, 300);

  const select = (value: string | number | string[] | number[] | null) => {
    if (value === null) {
      company.value = null;
      return;
    }
    company.value = companies.value.find(({ id }) => id === Number(value)) ?? company.value;
  };
</script>

<template>
  <Select
    :model-value="company?.id ?? null"
    :options="options"
    :loading="loading"
    placeholder="Search a HubSpot company"
    remote
    filterable
    clearable
    @search="search"
    @update:model-value="select" />
</template>
